import React, { useContext, useEffect, useMemo, useRef } from "react";
import { AxiosInstance } from "axios";
import { useQueryClient } from "react-query";

import { useCurrentTenant } from "contexts/currentTenant";
import useCsrfToken from "hooks/useCsrfToken";
import ocClient from "services/ocClient";
import { X_CSRF_TOKEN, X_TENANT_VERSION } from "utils/httpHeaderNames";

import { useLocale } from "./locale";

const VersionedClientContext = React.createContext<AxiosInstance | undefined>(undefined);

const VersionedClientProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const { version_number: versionNumber } = useCurrentTenant();
  const lastVersionRef = useRef(versionNumber);
  const csrfToken = useCsrfToken();
  const locale = useLocale();

  const client = useMemo(() => {
    const instance = ocClient.create({
      headers: {
        [X_TENANT_VERSION]: versionNumber,
        [X_CSRF_TOKEN]: csrfToken,
      },
    });

    instance.interceptors.request.use((config) => {
      const url = new URL(config.url, window.location.origin);
      url.searchParams.set("locale", locale);
      config.url = url.toString();
      return config;
    });

    return instance;
  }, [versionNumber, csrfToken, locale]);

  useEffect(() => {
    // if (lastVersionRef.current === versionNumber) {
    //   return;
    // }

    lastVersionRef.current = versionNumber;
    queryClient.invalidateQueries({
      predicate: (query) => {
        const p = query?.meta?.versionedEndpoint;
        if (p)
          console.debug(
            "VersionedClientProvider.invalidateQueries",
            query?.queryKey,
            versionNumber,
          );
        return !!p;
      },
    });
  }, [versionNumber, queryClient]);
  return (
    <VersionedClientContext.Provider value={client}>{children}</VersionedClientContext.Provider>
  );
};

export default VersionedClientProvider;

export const useVersionedClient = () => useContext(VersionedClientContext);
export const useVersionedClientRef = () => {
  const client = useContext(VersionedClientContext);
  const clientRef = useRef<AxiosInstance | null>(client);

  useEffect(() => {
    clientRef.current = client;
  }, [client]);

  return clientRef;
};
